import {Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ApiService} from '../../services/api/api.service';
import {ActivatedRoute} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DataStateChangeEvent, GridComponent, GridDataResult, PageChangeEvent, RowArgs,
  SelectionEvent
} from '@progress/kendo-angular-grid';
import {State} from '@progress/kendo-data-query';
import {VisitComponent} from './visit/visit.component';
import {NoteDialogComponent} from './note-dialog.component';
import {AgmMap} from '@agm/core';
import {AccountDialogComponent} from '../../accounts/detail/components/account-dialog/account-dialog.component';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {ClientDialogComponent} from '../../accounts/detail/components/client-dialog/client-dialog.component';
import {ShopifyService} from '../../services/shopify/shopify.service';
import {EventClickEvent, SchedulerEvent} from '@progress/kendo-angular-scheduler';
import {AppointmentDialogComponent} from '../../appointments/appointment-dialog.component';
import {UserContext} from '../../services/user-context';
import * as moment from 'moment';
import {ClientSessionService} from '../client-sessions.service';

@Component({
  selector: 'app-client-detail',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'client-detail.template.html',
  styleUrls: [
    './client-detail.style.scss'
  ]
})
export class ClientDetailComponent implements OnInit, OnDestroy {
  @ViewChild('visitsGrid', {static: true}) visitsGrid: GridComponent;

  public crumbs: any[] = [
    {label: 'Clients', link: '/clients'}
  ];

  public visitState: State = {
    skip: 0,
    take: 4
  };
  public notesState: State = {
    skip: 0,
    take: 5
  };
  public diagnosticsState: State = {
    skip: 0,
    take: 5
  };
  public prescriptionsState: State = {
    skip: 0,
    take: 5
  };
  public sessionState: State = {
    skip: 0,
    take: 5
  };

  public orthoticsGridData: GridDataResult;
  private selectedNote: RowArgs;
  public orthoticsState: State = {skip: 0, take: 5};

  public selectedVisitKeys: any[];
  appointments:SchedulerEvent[];
  private clientId: string;
  public client: any;
  private account: any;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  noteTypes: Array<any> = [{
    text: 'Simple',
    type: "simple"
  }, {
    text: 'SOAP',
    type: 'soap'
  }, {
    text: 'Assessment',
    type: 'assessment'
  }, {
    text: 'Consultation',
    type: 'consultation'
  }];
  notes: any;
  diagnostics: any;
  prescriptions: any;

  constructor(private route: ActivatedRoute,
              public dialog: MatDialog,
              private shopService: ShopifyService,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer,
              private userContext: UserContext,
              private snackBar: MatSnackBar,
              private api: ApiService) {
    this.matIconRegistry.addSvgIcon(
      `rx`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/prescription.svg')
    );
  }


  public ngOnInit(): void {
    this.clientId = this.route.snapshot.paramMap.get('id');

    this.refresh();
  }

  public refresh(): void {
    this.api.get<any>(`/client/${this.clientId}`)
      .subscribe(result => {
        this.client = result;
        this.notes = {total: (this.client.notes || []).length, data: (this.client.notes || []).reverse()};
        this.diagnostics = {total:(this.client.diagnostics || []).length, data: (this.client.diagnostics || []).reverse()};
        this.prescriptions = {total:(this.client.prescriptions || []).length, data: (this.client.prescriptions || []).reverse()};
        this.shopService.setShopForCountry(this.client.country || 'us');
        this.updateAppointments();
      }, (error) => {
        console.log('Found error ' + JSON.stringify(error));
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngAfterViewInit(): void {
  }

  public sessionStateChange(state: DataStateChangeEvent): void {
    this.sessionState = state;
  }

  public updateAppointments(): void {
    this.api.get<any>(`/client/${this.client.id}/appointment` ).subscribe( result => {
      this.appointments = (result || []).map( a => <SchedulerEvent> {
        title: a.type || "Appointment",
        subject: a.subject,
        id: a.id,
        type: a.type,
        confirmed: a.confirmed,
        location_id: a.location_id,
        account_id: a.account_id,
        name: a.name,
        user_id: a.user_id,
        client_id: a.client_id,
        duration: a.duration || 30,
        providerName: a.providerName,
        when: a.when,
        description: `${a.type || ""} Appointment`,
        start: moment(a.when).toDate(),
        end: moment(a.when).add(a.duration || 30, 'minutes').toDate()
        });
      //console.log("Have appointments: " + JSON.stringify(this.appointments));
      }, (error) => {
      this.snackBar.open(`Error getting appointments: ${error}`, null, {
        duration: 2000
      });
    });
  }

  public resendPortalInvite(): void {
    this.api.post<any>(`/client/${this.clientId}/portal`, {id: this.clientId}).subscribe( result => {
      this.snackBar.open('Portal Email Resent', null, {
        duration: 2000
      });
    }, (error) => {
      this.snackBar.open(`Error sending portal information: ${error}`, null, {
        duration: 2000
      });
    });
  }

  public editClient(): void {
    const dialogRef = this.dialog.open(ClientDialogComponent, {
      width: '750px',
      data: this.client
    });

    dialogRef.afterClosed().subscribe(account => {
      if ( account ) {
        this.api.patch<any>(`/client/${this.clientId}`, {
          firstName: this.client.firstName,
          lastName: this.client.lastName,
          email: this.client.email,
          occupation: this.client.occupation,
          dob: this.client.dob,
          mobile: this.client.mobile,
          phone: this.client.phone,
          address: this.client.address,
          address2: this.client.address2,
          city: this.client.city,
          region: this.client.region,
          postal: this.client.postal,
          country: this.client.country,
          biometric: this.client.biometric
        }).subscribe( result => {
          //console.log("Updated client respoonse: " + JSON.stringify(result));
          this.client = result;
          this.snackBar.open('Client updated.', null, {
            duration: 2000
          });
        }, (error) => {
          this.snackBar.open(`Error updating information: ${error}`, null, {
            duration: 2000
          });
        });
      }
    });
  }

  public updateProfile(): void {
    this.api.patch<any>(`/client/${this.clientId}`, {
      emergencyContact: this.client.emergencyContact,
      emergencyContactPhone: this.client.emergencyContactPhone,
      primaryCareName: this.client.primaryCareName,
      primaryCarePhone: this.client.primaryCarePhone
    }).subscribe(patchResult => {
      this.client = patchResult;
      this.snackBar.open('Client contacts updated.', null, {
        duration: 2000
      });
    }, (error) => {
      this.snackBar.open(`Error updating contacts: ${error}`, null, {
        duration: 2000
      });
    });
  }

  public clickedTelehealthAppointment(event: EventClickEvent) {
    const dialogRef = this.dialog.open(AppointmentDialogComponent, {
      disableClose: true,
      data: {id: event.event.id, dataItem: event.event.dataItem, client: this.client},
      width: '750px'
    });

    dialogRef.afterClosed().subscribe(appointment => {

      if (appointment) {
        if( appointment.cancel ) {
          this.api.del<any>(`/appointment/${appointment.id}`).subscribe(result => {
            this.snackBar.open('Appointment Canceled', null, {
              duration: 2000
            });
            this.updateAppointments();
            this.userContext.fetchRequests();
          }, (error) => {
            this.snackBar.open(`Error cancelling appointment: ${error}`, null, {
              duration: 2000
            });
          });
        } else {
          //console.log("Will update appointment: " + JSON.stringify(appointment, null, 4));
          this.api.patch<any>(`/appointment/${appointment.id}`, appointment).subscribe(result => {
            this.snackBar.open('Appointment Updated', null, {
              duration: 2000
            });
            this.updateAppointments();
            this.userContext.fetchRequests();
          }, (error) => {
            this.snackBar.open(`Error updating appointment: ${error}`, null, {
              duration: 2000
            });
          });
        }
      }
    });
  }

  public scheduleTelehealthAppointment() {
    const dialogRef = this.dialog.open(AppointmentDialogComponent, {
      disableClose: true,
      data: {client: this.client},
      width: '750px'
    });

    dialogRef.afterClosed().subscribe(appointment => {

      if (appointment) {
        //console.log("Will update appointment: " + JSON.stringify(appointment, null,4));
        this.api.post<any>(`/client/${(this.client.id)}/appointment`, appointment ).subscribe( result => {
          this.snackBar.open('Appointment Confirmed', null, {
            duration: 2000
          });
          this.updateAppointments();
          this.userContext.fetchRequests();
        }, (error) => {
          this.snackBar.open(`Error updating appointment: ${error}`, null, {
            duration: 2000
          });
        });
      }
    });
  }

  public selectedVisit(event: SelectionEvent) {
    // Ask for shipping information
    const dialogRef = this.dialog.open(VisitComponent, {
      width: window.screen.width >= 800 ? '800px' : '250px',
      data: event.selectedRows[0].dataItem
    });

    dialogRef.afterClosed().subscribe(result => {
       // TODO deselect rows (not suppored by kendo yet)
      this.selectedVisitKeys = [];
    });
  }

  public visitStateChange(state: DataStateChangeEvent): void {
    this.visitState = state;
  }

  public diagnosticsStateChange(state: DataStateChangeEvent): void {
    this.diagnosticsState = state;
  }

  public notesStateChange(state: DataStateChangeEvent): void {
    this.notesState = state;
  }

  public prescriptionsStateChange(state: DataStateChangeEvent): void {
    this.prescriptionsState = state;
  }

  public onNoteSelection(event: SelectionEvent) {
    this.selectedNote = event.selectedRows[0];
  }

  public addNote(noteType:any): void {
    const dialogRef = this.dialog.open(NoteDialogComponent, {
      width: "700px",
      maxHeight: "95vh",
      maxWidth: "50vw",
      data: {type: noteType.type},
    });

    dialogRef.afterClosed().subscribe(note => {

      if ( note ) {

        this.api.post<any>(`/client/${this.clientId}/note`, note ).subscribe( result => {
          this.snackBar.open('Note added.', null, {
            duration: 2000
          });
          this.refresh();
        }, (error) => {
          this.snackBar.open(`Error adding note: ${error}`, null, {
            duration: 2000
          });
        });

        if( note.meta.cart ) {
          console.log("Generating cart...");
          this.api.post<any>(`/client/${this.clientId}/cart`, {
            user_id: this.userContext.user.user_id,
            picture: this.userContext.user.picture,
            curator: `${this.userContext.user.name} ${this.userContext.user.titles}`.trim(),
            lineItems: note.meta.products
          }).subscribe( result => {
            let cart = result;
            console.log("Cart: " + JSON.stringify(cart,null,4));
          });
        }
      }
    });
  }

  public viewNote(): void {
    const dialogRef = this.dialog.open(NoteDialogComponent, {
      width: "600px",
      maxWidth: "50vw",
      maxHeight: "95vh",
      data: this.selectedNote.dataItem
    });

    dialogRef.afterClosed().subscribe(location => {
      this.selectedNote = null;
    });
  }

  public updateBiometric(): void {
    this.api.patch<any>(`/client/${this.clientId}`, {
      biometric: this.client.biometric
    }).subscribe(patchResult => {
      this.client = patchResult;
      this.snackBar.open('Client biometrics updated.', null, {
        duration: 2000
      });
    }, (error) => {
      this.snackBar.open(`Error updating biometrics: ${error}`, null, {
        duration: 2000
      });
    });
  }

  protected orthoticsPageChange(event: PageChangeEvent): void {
    this.orthoticsState.skip = event.skip;
    this.loadOrthoticsData();
  }


  protected loadOrthoticsData() {
    this.orthoticsGridData = {
      data: (this.client.orthotics || []).slice(this.orthoticsState.skip, this.orthoticsState.skip + this.orthoticsState.take),
      total: (this.client.orthotics || []).length
    };
  }

}
